import { Box, Grid, CircularProgress, Button, Typography, Tooltip, IconButton } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, Checkbox } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomSnackbar from 'components/CustomSnackbar';
import { ControllerCard } from '../../components/Controllers/ControllerCard';
import { SimpleControllerCard } from '../../components/Controllers/SimpleControllerCard';
import MDTypography from 'components/MDTypography/index';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPage, fetchUsersDetails } from '../../stores/sensor/sensorStore';
import { ScheduleCard } from './ScheduleCard';
import { SchedulesFilterBar } from './SchedulesFilterBar';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { brandColors } from 'types/colors';
import {
  fetchSchedulesDetails,
  fetchControllers,
  saveRecommendationForDevices,
  activateRecommendationsForDevices,
} from 'stores/sensor/controllersSlice';
import { clear } from 'stores/sensor/controllersSlice';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DevicesListFilter from 'components/DevicesFilter/DevicesListFilter.js';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

export const SchedulesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get metadata schedules from state
  const schedules = useSelector(s => s.controllers.schedules || []);
  const schedulesStatus = useSelector(s => s.controllers.schedulesStatus);
  const schedulesError = useSelector(s => s.controllers.schedulesError);
  const loading = schedulesStatus === 'loading';
  // const filteredControllers = useSelector(s => s.controllers.controllers || []);
  const filteredControllers = useSelector(s => s.controllers.filteredControllers || []);
  const allControllers = useSelector(s => s.controllers.controllers || []);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(['ALL']);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Open generate modal
  const handleGenerateRecommendation = () => {
    setShowGenerateModal(true);
  };

  // Confirm generate recommendations
  const handleConfirmGenerate = async () => {
    if (selectedDevices.length === 0) return;

    setIsGenerating(true);

    try {
      const cleanDeviceIds = [...new Set(selectedDevices.map(id => Number(id)))];

      await dispatch(
        saveRecommendationForDevices({
          deviceIds: cleanDeviceIds,
        }),
      ).unwrap();
      dispatch(fetchSchedulesDetails());

      setSnack({
        open: true,
        message: `Recommendations generated for ${selectedDevices.length} device(s)`,
        severity: 'success',
      });

      setShowGenerateModal(false);
      setSelectedDevices([]);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to generate recommendations: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Open activate modal
  const handleActivateRecommendations = async () => {
    setShowActivateModal(true);
  };

  // Confirm activate recommendations
  const handleConfirmActivate = async () => {
    if (selectedDevices.length === 0) return;

    setIsActivating(true);

    try {
      const cleanDeviceIds = [...new Set(selectedDevices.map(id => Number(id)))];

      // Call activation API for each device
      await dispatch(activateRecommendationsForDevices(cleanDeviceIds)).unwrap();

      setSnack({
        open: true,
        message: `Recommendations activated for ${selectedDevices.length} device(s)`,
        severity: 'success',
      });

      setShowActivateModal(false);
      setSelectedDevices([]);
    } catch (error) {
      setSnack({
        open: true,
        message: `Failed to activate recommendations: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setIsActivating(false);
    }
  };

  // Toggle device selection
  const toggleDeviceSelection = deviceId => {
    const normalizedId = Number(deviceId);
    setSelectedDevices(prev => {
      if (prev.includes(normalizedId)) {
        return prev.filter(id => id !== normalizedId);
      }
      return [...prev, normalizedId];
    });
  };

  // Reset selected devices when modal opens
  useEffect(() => {
    if (showGenerateModal || showActivateModal) {
      setSelectedDevices([]);
    }
  }, [showGenerateModal, showActivateModal]);

  useEffect(() => {
    dispatch(setCurrentPage('schedule-list'));
    dispatch(fetchSchedulesDetails());
    dispatch(fetchControllers());
  }, [dispatch]);

  useEffect(() => {
    return () => dispatch(clear());
  }, []);

  const activeScheduleIds = useMemo(() => {
    // const ids = filteredControllers
    const ids = allControllers
      .map(controller => controller.active_schedule_id)
      .filter(id => id !== null && id !== undefined && id !== '');
    return new Set(ids.map(id => String(id)));
  }, [allControllers]);

  const filteredSchedules = useMemo(() => {
    if (selectedFilters.includes('ALL')) {
      return schedules;
    }

    const statusFilters = new Set(selectedFilters.filter(filterKey => filterKey === 'ACTIVE' || filterKey === 'INACTIVE'));
    const isRecommendationsOnly = selectedFilters.includes('RECOMMENDATIONS');
    const isManualOnly = selectedFilters.includes('MANUAL');
    const scopeFilters = new Set(selectedFilters.filter(filterKey => filterKey === 'GLOBAL' || filterKey === 'SPECIFIC'));

    return schedules.filter(schedule => {
      const isActive = activeScheduleIds.has(String(schedule.schedule_id));
      const isGlobal = Boolean(schedule.is_global);
      const isRecommendation = Boolean(schedule.is_auto_generated);
      const matchesStatus =
        statusFilters.size === 0 || (isActive && statusFilters.has('ACTIVE')) || (!isActive && statusFilters.has('INACTIVE'));
      const matchesScope =
        scopeFilters.size === 0 || (isGlobal && scopeFilters.has('GLOBAL')) || (!isGlobal && scopeFilters.has('SPECIFIC'));

      const matchesOrigin =
        (!isRecommendationsOnly && !isManualOnly) ||
        (isRecommendationsOnly && isRecommendation) ||
        (isManualOnly && !isRecommendation);

      return matchesStatus && matchesScope && matchesOrigin;
    });
  }, [schedules, selectedFilters, activeScheduleIds]);

  const activeSchedules = useMemo(
    () => filteredSchedules.filter(schedule => activeScheduleIds.has(String(schedule.schedule_id))),
    [filteredSchedules, activeScheduleIds],
  );

  const inactiveSchedules = useMemo(
    () => filteredSchedules.filter(schedule => !activeScheduleIds.has(String(schedule.schedule_id))),
    [filteredSchedules, activeScheduleIds],
  );

  const allFilteredDeviceIds = useMemo(
    () => [...new Set(filteredControllers.map(controller => Number(controller.id)))],
    [filteredControllers],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} mt={1}>
      {/* ===== HEADER===== */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <MDTypography variant="h4" sx={{ ml: 0.2, color: 'black' }}>
          Manage Schedules
        </MDTypography>
      </Box>

      {/* ===== MAIN CONTENT ===== */}
      <Box sx={{ display: 'flex', flexDirection: 'row' }} mt={1}>
        <Box ml={1} mb={1} sx={{ width: '100%' }}>
          {/* ===== BUTTONS CONTENT ===== */}
          {!loading && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, ml: 1 }}>
              <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => navigate('/manage-schedules/new')}>
                New Global Schedule
              </Button>
              <Button startIcon={<AutoAwesomeIcon />} variant="contained" color="primary" onClick={handleGenerateRecommendation}>
                Generate Recommendations
              </Button>
              <Button startIcon={<PlayArrowIcon />} variant="contained" color="primary" onClick={handleActivateRecommendations}>
                Activate Recommendations
              </Button>
            </Box>
          )}

          <SchedulesFilterBar selectedFilters={selectedFilters} onChange={setSelectedFilters} />

          {/* ===== LOADING STATE ===== */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {/* ===== SCHEDULES GRID ===== */}
          {!loading && filteredSchedules.length > 0 && (
            <Grid
              className="schedulesListContainer"
              sx={{
                display: 'flex',
                alignItems: 'start',
                height: 'fit-content',
                maxHeight: '71vh',
                overflow: 'auto',
                width: '88vw',
                flexWrap: 'wrap',
                overflowX: 'hidden',
              }}
              margin={0}>
              {/* ACTIVE SCHEDULES */}
              {activeSchedules.length > 0 && (
                <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'start', px: '1rem' }}>
                  <Box sx={{ width: '100%', my: 2, px: 1 }} id="BBBBBBBB">
                    <MDTypography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                      Active Schedules
                    </MDTypography>
                  </Box>
                  {activeSchedules.map(schedule => (
                    <Grid
                      key={schedule.id}
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={3}
                      sx={{
                        lineHeight: 0,
                        position: 'relative',
                        marginBottom: '1rem',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'start' },
                      }}>
                      <ScheduleCard
                        scheduleName={schedule.schedule_name}
                        scheduleId={schedule.schedule_id}
                        status={schedule.status}
                        device_count={schedule.device_count}
                        approved_by_user_id={schedule.approved_by_user_id}
                        activated_timestamp={schedule.activated_timestamp}
                        building_ids={schedule.building_ids}
                        area_ids={schedule.area_ids}
                        devices={schedule.devices}
                        is_auto_generated={schedule.is_auto_generated}
                        is_global={schedule.is_global}
                      />
                    </Grid>
                  ))}
                </Box>
              )}

              {/* SEPARATOR */}
              {activeSchedules.length > 0 && inactiveSchedules.length > 0 && (
                <Box sx={{ width: '100%', my: 2, px: 2 }}>
                  <Box sx={{ borderBottom: '2px solid #e0e0e0', mb: 2 }} />
                  <MDTypography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                    Inactive Schedules
                  </MDTypography>
                </Box>
              )}

              {/* INACTIVE SCHEDULES */}
              {inactiveSchedules.length > 0 && (
                <Box sx={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'start', px: '1rem' }}>
                  {activeSchedules.length === 0 && (
                    <Box sx={{ width: '100%', my: 2, px: 2 }}>
                      <MDTypography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                        Inactive Schedules
                      </MDTypography>
                    </Box>
                  )}
                  {inactiveSchedules.map(schedule => (
                    <Grid
                      key={schedule.id}
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={3}
                      sx={{
                        lineHeight: 0,
                        position: 'relative',
                        marginBottom: '1rem',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'start' },
                      }}>
                      <ScheduleCard
                        scheduleName={schedule.schedule_name}
                        scheduleId={schedule.schedule_id}
                        status={schedule.status}
                        device_count={schedule.device_count}
                        approved_by_user_id={schedule.approved_by_user_id}
                        activated_timestamp={schedule.activated_timestamp}
                        building_ids={schedule.building_ids}
                        area_ids={schedule.area_ids}
                        devices={schedule.devices}
                        is_auto_generated={schedule.is_auto_generated}
                        is_global={schedule.is_global}
                      />
                    </Grid>
                  ))}
                </Box>
              )}

              {/* SEPARATOR RECOMMENDATIONS */}
              {/* <Box sx={{ width: '100%', my: 2, px: 2 }}>
                <Box sx={{ borderBottom: '2px solid #e0e0e0', mb: 2 }} />
                <MDTypography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  Inactive Recommendations
                </MDTypography>
              </Box>

              {schedules
                .filter(
                  schedule =>
                    schedule.status !== 'active' &&
                    schedule.status !== 'active_not_synced' &&
                    schedule.is_auto_generated == true,
                )
                .map(schedule => (
                  <Grid
                    key={schedule.id}
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={4}
                    xl={3}
                    sx={{
                      lineHeight: 0,
                      position: 'relative',
                      marginBottom: '1rem',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: { xs: 'center', sm: 'start' },
                    }}>
                    <ScheduleCard
                      scheduleName={schedule.schedule_name}
                      scheduleId={schedule.schedule_id}
                      status={schedule.status}
                      device_count={schedule.device_count}
                      approved_by_user_id={schedule.approved_by_user_id}
                      activated_timestamp={schedule.activated_timestamp}
                      building_ids={schedule.building_ids}
                      area_ids={schedule.area_ids}
                      devices={schedule.devices}
                      is_auto_generated={schedule.is_auto_generated}
                      is_global={schedule.is_global}
                    />
                  </Grid>
                ))} */}
            </Grid>
          )}

          {/* ===== EMPTY STATE ===== */}
          {!loading && filteredSchedules.length === 0 && schedulesStatus === 'succeeded' && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="text">
                No schedules to display
              </MDTypography>
            </Box>
          )}

          {/* ===== ERROR STATE ===== */}
          {!loading && schedulesStatus === 'failed' && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <MDTypography variant="h6" color="error">
                Failed to load schedules
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                {schedulesError || 'Please try refreshing the page'}
              </MDTypography>
            </Box>
          )}

          {/* Generate Recommendations Modal */}
          <Dialog open={showGenerateModal} onClose={() => setShowGenerateModal(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
              Select Devices for Recommendation Generation
              <Tooltip
                title="Automatically generates tailored recommendations for each controller individually."
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                    },
                  },
                }}>
                <IconButton size="small" onClick={event => event.stopPropagation()}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </DialogTitle>

            <Box sx={{ padding: '1rem' }}>
              <DevicesListFilter type="controllers" />
            </Box>

            <DialogContent sx={{ minHeight: '400px' }}>
              {filteredControllers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>No controllers available</Typography>
                </Box>
              ) : (
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {filteredControllers.map(controller => (
                    <Grid item xs={12} sm={6} md={4} key={controller.id}>
                      <Box
                        onClick={() => toggleDeviceSelection(controller.id)}
                        sx={{
                          position: 'relative',
                          opacity: selectedDevices.includes(controller.id) ? 1 : 0.6,
                          transition: 'opacity 0.2s',
                          cursor: 'pointer',
                          '&:hover': { opacity: 1 },
                          width: 'fit-content',
                          border: selectedDevices.includes(controller.id) ? `3px solid ${brandColors.BLUE}` : '3px solid #fff',
                          borderRadius: '6px',
                        }}>
                        <SimpleControllerCard
                          id={controller.id}
                          name={controller.name}
                          building_name={controller.building_name}
                          floorNumber={controller.floor_number}
                          area_name={controller.area_name}
                          deviceType="controller"
                          onCardClick={e => {
                            e.preventDefault();
                          }}
                          hoverAnimation={false}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Box className="select">
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={allFilteredDeviceIds.length === 0 || selectedDevices.length === allFilteredDeviceIds.length}
                      onClick={() => setSelectedDevices(allFilteredDeviceIds)}
                      sx={{ color: '#000' }}>
                      Select all
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={selectedDevices.length === 0}
                      onClick={() => setSelectedDevices([])}
                      sx={{ color: '#000' }}>
                      Unselect all
                    </Button>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedDevices.length} device(s) selected
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => {
                      setShowGenerateModal(false);
                      setSelectedDevices([]);
                    }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmGenerate}
                    disabled={selectedDevices.length === 0 || isGenerating}
                    variant="contained">
                    {isGenerating ? <CircularProgress size={24} /> : 'Generate Recommendations'}
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Activate Recommendations Modal */}
          <Dialog open={showActivateModal} onClose={() => setShowActivateModal(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
              Select Devices to Activate Recommendations
              <Tooltip
                title="Choose which controllers will activate their auto-generated recommendation schedules."
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                    },
                  },
                }}>
                <IconButton size="small" onClick={event => event.stopPropagation()}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </DialogTitle>

            <Box sx={{ padding: '1rem' }}>
              <DevicesListFilter type="controllers" />
            </Box>

            <DialogContent sx={{ minHeight: '400px' }}>
              {filteredControllers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>No controllers available</Typography>
                </Box>
              ) : (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {filteredControllers.map(controller => (
                    <Grid item xs={12} sm={6} md={4} key={controller.id}>
                      <Box
                        className="controller-container"
                        onClick={() => toggleDeviceSelection(controller.id)}
                        sx={{
                          position: 'relative',
                          opacity: selectedDevices.includes(controller.id) ? 1 : 0.6,
                          transition: 'opacity 0.2s',
                          cursor: 'pointer',
                          '&:hover': { opacity: 1 },
                          width: 'fit-content',
                          border: selectedDevices.includes(controller.id) ? `3px solid ${brandColors.BLUE}` : '3px solid #fff',
                          borderRadius: '6px',
                        }}>
                        <SimpleControllerCard
                          id={controller.id}
                          name={controller.name}
                          building_name={controller.building_name}
                          floorNumber={controller.floor_number}
                          area_name={controller.area_name}
                          deviceType="controller"
                          onCardClick={e => {
                            e.preventDefault();
                          }}
                          hoverAnimation={false}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Box className="select">
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={allFilteredDeviceIds.length === 0 || selectedDevices.length === allFilteredDeviceIds.length}
                      onClick={() => setSelectedDevices(allFilteredDeviceIds)}
                      sx={{ color: '#000' }}>
                      Select all
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={selectedDevices.length === 0}
                      onClick={() => setSelectedDevices([])}
                      sx={{ color: '#000' }}>
                      Unselect all
                    </Button>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedDevices.length} device(s) selected
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => {
                      setShowActivateModal(false);
                      setSelectedDevices([]);
                    }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmActivate}
                    disabled={selectedDevices.length === 0 || isActivating}
                    variant="contained">
                    {isActivating ? <CircularProgress size={24} /> : 'Activate Recommendations'}
                  </Button>
                </Box>
              </Box>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <CustomSnackbar
            {...snack}
            onClose={() => setSnack(s => ({ ...s, open: false }))}
            duration={4000}
            vertical="top"
            horizontal="right"
            color="#fff"
          />
        </Box>
      </Box>
    </Box>
  );
};
